import { useRuntimeConfig } from "#imports";

const LEGACY_PUBLIC_R2_URLS = [
  "https://cdn.sharkfoto.com",
  "https://sharkfoto-cdn.zheng-yun.com",
  "https://pub-8f96a058cbeb4650ac7775f23dca06cc.r2.dev",
];

const LEGACY_USER_R2_URLS = [
  "https://storage.sharkfoto.com",
  "https://pub-a493cbae12d24ee498bfe20e3daa78f6.r2.dev",
];

function trimTrailingSlash(value) {
  return typeof value === "string" ? value.replace(/\/+$/, "") : "";
}

function isAbsoluteAssetUrl(value) {
  return /^(https?:)?\/\//i.test(value) || /^(blob|data):/i.test(value);
}

function joinUrl(base, path) {
  const normalizedBase = trimTrailingSlash(base);
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  if (!normalizedBase || !normalizedPath) return normalizedPath || normalizedBase;
  return `${normalizedBase}/${normalizedPath}`;
}

function currentRuntimeConfig() {
  const fallback = {
    cdnUrl: typeof __CDN_URL__ !== "undefined" ? __CDN_URL__ : "",
    userR2Url: typeof __USER_R2_URL__ !== "undefined" ? __USER_R2_URL__ : "",
  };
  try {
    return {
      ...fallback,
      ...(useRuntimeConfig().public || {}),
    };
  } catch {
    return fallback;
  }
}

export function stripKnownR2Domain(value) {
  if (typeof value !== "string") return value;
  const raw = value.trim();
  if (!raw) return raw;

  const config = currentRuntimeConfig();
  const prefixes = [
    config.cdnUrl,
    config.userR2Url,
    "/api/r2/user",
    ...LEGACY_PUBLIC_R2_URLS,
    ...LEGACY_USER_R2_URLS,
  ]
    .map(trimTrailingSlash)
    .filter(Boolean);

  for (const prefix of prefixes) {
    if (raw === prefix) return "";
    if (raw.startsWith(`${prefix}/`)) {
      return raw.slice(prefix.length).replace(/^\/+/, "");
    }
  }

  if (!isAbsoluteAssetUrl(raw)) return raw.replace(/^\/+/, "");
  return raw;
}

function stripKnownUserR2Domain(value) {
  if (typeof value !== "string") return value;
  const raw = value.trim();
  if (!raw) return raw;

  const config = currentRuntimeConfig();
  const prefixes = [
    config.userR2Url,
    "/api/r2/user",
    ...LEGACY_USER_R2_URLS,
  ]
    .map(trimTrailingSlash)
    .filter(Boolean);

  for (const prefix of prefixes) {
    if (raw === prefix) return "";
    if (raw.startsWith(`${prefix}/`)) {
      return raw.slice(prefix.length).replace(/^\/+/, "");
    }
  }

  if (!isAbsoluteAssetUrl(raw)) return raw.replace(/^\/+/, "");
  return raw;
}

export function resolveR2Url(value, bucket = "public") {
  if (typeof value !== "string") return value || "";
  let raw = value.trim();
  if (!raw) return raw;

  if (bucket === "user") {
    raw = stripKnownUserR2Domain(raw);
  }

  if (isAbsoluteAssetUrl(raw)) return raw;

  const config = currentRuntimeConfig();
  if (bucket === "user" && !import.meta.dev) {
    return joinUrl("/api/r2/user", raw);
  }

  const base = bucket === "user" ? config.userR2Url : config.cdnUrl;
  return joinUrl(base, raw);
}

export function resolvePublicR2Url(value) {
  return resolveR2Url(value, "public");
}

export function resolveUserR2Url(value) {
  return resolveR2Url(value, "user");
}

export function resolveUserR2UrlList(value) {
  if (Array.isArray(value)) return value.map(resolveUserR2Url).filter(Boolean);
  const resolved = resolveUserR2Url(value);
  return resolved ? [resolved] : [];
}

/** 将 R2 对象 key 或存储 URL 解析为可安全 fetch 的地址 */
export function resolveFetchAssetUrl(value) {
  if (typeof value !== "string") return value || "";
  const raw = value.trim();
  if (!raw || /^(blob|data):/i.test(raw)) return raw;

  const objectKey = stripKnownR2Domain(raw);
  if (objectKey.startsWith("ai/tasks/")) {
    return resolveUserR2Url(objectKey);
  }

  if (isAbsoluteAssetUrl(raw)) return raw;

  return resolvePublicR2Url(objectKey);
}
